import React from "react";
import { Link } from "react-router-dom";
import { Linkedin, Instagram, Facebook, Mail, GraduationCap, ArrowUpRight } from "lucide-react";

export const FooterSection = () => {
  const currentYear = new Date().getFullYear();

  return (
    <footer className="bg-navy border-t border-white/5 py-16 lg:py-24 relative overflow-hidden text-slate-400">
      {/* Visual background accents */}
      <div className="absolute top-0 right-0 w-80 h-80 bg-gold/5 rounded-full blur-[120px] pointer-events-none" />
      <div className="absolute bottom-0 left-0 w-80 h-80 bg-white/5 rounded-full blur-[120px] pointer-events-none" />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-12 relative z-10">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-12 gap-12 lg:gap-8 items-start mb-16">
          
          {/* Main Info Column */}
          <div className="lg:col-span-5 flex flex-col space-y-4">
            <Link to="/" data-testid="footer-logo-link" className="flex flex-col">
              <span className="font-display text-2xl font-bold tracking-tight text-white">
                SUHASINI BASA
              </span>
              <span className="text-xs uppercase tracking-[0.15em] text-gold font-mono mt-1">
                Career Transformation Coach
              </span>
            </Link>
            <p className="text-sm max-w-sm leading-relaxed mt-2 text-slate-300">
              Specialising in helping experienced IT Business Analysts and Project Managers develop the confidence, visibility, and influence required to become recognized as strategic leaders.
            </p>
            {/* Social Icons */}
            <div className="flex items-center space-x-4 pt-2">
              <a
                href="https://www.linkedin.com/in/suhasini-basa-pmp"
                target="_blank"
                rel="noopener noreferrer"
                data-testid="footer-social-linkedin"
                className="w-10 h-10 rounded-none bg-white/5 border border-white/10 hover:border-gold hover:text-gold flex items-center justify-center transition-colors duration-200 text-white"
                aria-label="LinkedIn"
              >
                <Linkedin className="w-4 h-4" />
              </a>
              <a
                href="https://www.instagram.com/suhasini.basa?igsh=YzVpbmYwejZxMnJy"
                target="_blank"
                rel="noopener noreferrer"
                data-testid="footer-social-instagram"
                className="w-10 h-10 rounded-none bg-white/5 border border-white/10 hover:border-gold hover:text-gold flex items-center justify-center transition-colors duration-200 text-white"
                aria-label="Instagram"
              >
                <Instagram className="w-4 h-4" />
              </a>
              <a
                href="https://www.facebook.com/share/19AC77VpW8/"
                target="_blank"
                rel="noopener noreferrer"
                data-testid="footer-social-facebook"
                className="w-10 h-10 rounded-none bg-white/5 border border-white/10 hover:border-gold hover:text-gold flex items-center justify-center transition-colors duration-200 text-white"
                aria-label="Facebook"
              >
                <Facebook className="w-4 h-4" />
              </a>
            </div>
          </div>

          {/* Quick Links Column */}
          <div className="lg:col-span-3 flex flex-col space-y-4">
            <h4 className="font-display text-sm font-semibold tracking-wider text-white uppercase font-mono">
              Navigation
            </h4>
            <div className="grid grid-cols-2 gap-2 text-sm">
              <Link to="/" data-testid="footer-link-home" className="hover:text-white transition-colors duration-200">
                Home
              </Link>
              <Link to="/about" data-testid="footer-link-about" className="hover:text-white transition-colors duration-200">
                About
              </Link>
              <Link to="/programs" data-testid="footer-link-programs" className="hover:text-white transition-colors duration-200">
                Programs
              </Link>
              <Link to="/coaching" data-testid="footer-link-coaching" className="hover:text-white transition-colors duration-200">
                1:1 Coaching
              </Link>
              <Link to="/free-resources" data-testid="footer-link-free-resources" className="hover:text-white transition-colors duration-200">
                Free Resources
              </Link>
              <Link to="/contact" data-testid="footer-link-contact" className="hover:text-white transition-colors duration-200 col-span-2">
                Contact
              </Link>
            </div>
          </div>

          {/* Learning Column */}
          <div className="lg:col-span-4 flex flex-col space-y-4">
            <h4 className="font-display text-sm font-semibold tracking-wider text-white uppercase font-mono">
              Ecosystem
            </h4>
            <div className="space-y-3">
              <a
                href="https://academy.suhasinibasa.com/"
                target="_blank"
                rel="noopener noreferrer"
                data-testid="footer-link-academy"
                className="flex items-center gap-2 text-sm text-slate-300 hover:text-gold transition-colors duration-200"
              >
                <GraduationCap className="w-4 h-4 text-gold" />
                <span>Career Influencer Hub</span>
                <ArrowUpRight className="w-3 h-3 opacity-60" />
              </a>
            </div>
          </div>

        </div>

        {/* Big Awwwards Typography Banner */}
        <div className="border-t border-white/10 pt-10 pb-6 text-center select-none overflow-hidden">
          <h2 className="font-display text-3xl sm:text-5xl md:text-7xl lg:text-[7.5rem] font-bold text-slate-800/40 tracking-tighter leading-none select-none uppercase font-heading">
            ELEVATE YOUR IMPACT
          </h2>
        </div>

        {/* Legal & Meta Information */}
        <div className="border-t border-white/5 pt-8 flex flex-col md:flex-row items-center justify-between gap-4 text-xs">
          <p data-testid="footer-copyright">
            &copy; {currentYear} Suhasini Basa. All rights reserved. Registered Funnel Builder.
          </p>
          <div className="flex items-center space-x-6">
            <Link to="/privacy-policy" data-testid="footer-link-privacy" className="hover:text-white transition-colors duration-200">
              Privacy Policy
            </Link>
            <Link to="/terms-conditions" data-testid="footer-link-terms" className="hover:text-white transition-colors duration-200">
              Terms &amp; Conditions
            </Link>
            <Link to="/refund-policy" data-testid="footer-link-refund" className="hover:text-white transition-colors duration-200">
              Refund Policy
            </Link>
          </div>
        </div>
      </div>
    </footer>
  );
};
